import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import { trackEvent } from "@/components/Analytics"
import { DASHBOARD_KEY } from "@/lib/hooks/use-dashboard"
import { showToast } from "@/lib/lazy-toast"
import { guideSlug, tripSlug } from "@/lib/slugify"

// The minimum a card needs to be copyable. short_id is optional: the explore
// grid has it (canonical share URL for the login bounce), but the destination
// hub's ExploreCardItem doesn't — the uuid `id` works as a fallback because the
// guide route resolves uuids too and the page canonicalizes onto the short id.
export type CopyableGuide = {
  id: string
  name?: string | null
  destination: string
  image_url?: string | null
  short_id?: string | null
}

// One copy funnel for every surface that copies a guide (explore, destination
// hubs). Was inline in GuidePage; lifted so each new grid reuses the exact same
// instrumentation, login-wall bounce, optimistic overlay and error toast rather
// than re-deriving them (and drifting the analytics). `source` keys the events
// to the surface so the funnel stays attributable per page.
export function useCopyGuide(source: string) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: session } = authClient.useSession()
  const [copyingGuide, setCopyingGuide] = useState<CopyableGuide | null>(null)

  const copyGuide = async (guide: CopyableGuide) => {
    // Copying needs an account — send signed-out visitors to login with a
    // redirect back to the guide carrying ?copy=1, which resumes the copy.
    const loginThenCopy = () =>
      navigate({
        to: `/login`,
        search: {
          redirect: `/guide/${guideSlug(guide)}/${guide.short_id ?? guide.id}?copy=1`,
        },
      })
    // Click vs. guide_duplicated below is the funnel: the gap for
    // signed_in:false clicks is the login-wall drop-off.
    trackEvent(`guide_copy_click`, {
      guide_id: guide.id,
      signed_in: !!session,
      source,
    })
    if (!session) {
      loginThenCopy()
      return
    }
    try {
      setCopyingGuide(guide)
      const result = await trpc.guides.duplicate.mutate({ id: guide.id })
      // The viral loop: discovery → copy → a new editable trip (activation).
      trackEvent(`guide_duplicated`, { guide_id: guide.id, source })
      // Refresh the dashboard cache so the header can find the new trip
      // (it reads the trip from DASHBOARD_KEY, not getTripData).
      await queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
      navigate({
        to: `/trip/$slug/$tripId`,
        params: { slug: tripSlug(result.item), tripId: result.item.short_id },
      })
    } catch (err: unknown) {
      setCopyingGuide(null)
      const status = (err as { data?: { httpStatus?: number } })?.data?.httpStatus
      if (status === 401 || status === 403) {
        loginThenCopy()
        return
      }
      // A server-side failure (e.g. the prod short_id NOT NULL outage) used to
      // end here silently — overlay gone, no trip, no explanation. Always tell
      // the user the copy didn't happen.
      void showToast({
        title: `Couldn't copy this guide`,
        description: `Something went wrong on our end. Please try again.`,
        color: `danger`,
      })
    }
  }

  return { copyingGuide, copyGuide }
}
